// Storage Utils Service - Diagnostics, Emergency Controls & Reporting
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog, debugError, debugWarn } from '../debugUtils';
import {
  getStorageAvailability,
  detectStorageEnvironment,
} from './storageDetection';
import {
  localStorageHelpers,
  sessionStorageHelpers,
  cookieHelpers,
  USER_DATA_KEY,
  removeFromStorage,
} from './storageHelpers';
import { getEncryptionStrategy } from './encryptionService';
// Note: Avoiding circular import with userDataService by implementing clearUserData directly

// Storage keys and configuration
const STORAGE_PREFIX = 'swts_'; // Sing with the Stars prefix
const ENCRYPTION_KEY_KEY = `${STORAGE_PREFIX}enc_key`;

/**
 * Get storage mechanism diagnostics
 */
export const getStorageDiagnostics = () => {
  const availability = getStorageAvailability();
  const diagnostics = {
    availability,
    currentData: {},
    inMemoryKeys: [],
  };

  // Try to read main data from each mechanism
  if (availability.localStorage) {
    diagnostics.currentData.localStorage =
      localStorageHelpers.get(USER_DATA_KEY);
  }
  if (availability.sessionStorage) {
    diagnostics.currentData.sessionStorage =
      sessionStorageHelpers.get(USER_DATA_KEY);
  }
  if (availability.cookies) {
    diagnostics.currentData.cookies = cookieHelpers.get(USER_DATA_KEY);
  }
  if (availability.urlParams) {
    const params = new URLSearchParams(window.location.search);
    diagnostics.currentData.urlParams = params.toString();
  }

  return diagnostics;
};

/**
 * Clear specific keys from all storage mechanisms (granular clearing)
 * @param {string|string[]} keys - Single key or array of keys to remove
 */
export const clearStorageKeys = keys => {
  if (typeof window === 'undefined') return;

  const keyArray = Array.isArray(keys) ? keys : [keys];
  const results = {};

  keyArray.forEach(key => {
    results[key] = removeFromStorage(key);
  });

  debugLog('🗑️ Cleared specific keys from all mechanisms:', {
    keys: keyArray,
    results,
  });
  return results;
};

/**
 * Emergency rollback function - disable encryption in production if needed
 * Usage: Call this function in browser console to disable encryption
 */
export const emergencyDisableEncryption = () => {
  if (typeof window === 'undefined') {
    debugLog('Not available server-side');
    return;
  }

  try {
    localStorage.setItem('swts_disable_encryption', 'true');
    // Clear existing data to force re-encryption with new strategy (avoiding circular import)
    removeFromStorage(USER_DATA_KEY);
    clearAllEncryptionKeys();
    debugWarn(
      '🚨 EMERGENCY: Encryption disabled. Refresh page to take effect.'
    );
    debugWarn('To re-enable encryption, call: emergencyEnableEncryption()');
  } catch (e) {
    debugError('Failed to disable encryption:', e);
  }
};

/**
 * Re-enable encryption after emergency rollback
 */
export const emergencyEnableEncryption = () => {
  if (typeof window === 'undefined') {
    debugLog('Not available server-side');
    return;
  }

  try {
    localStorage.removeItem('swts_disable_encryption');
    debugWarn('✅ Encryption re-enabled. Refresh page to take effect.');
  } catch (e) {
    debugError('Failed to re-enable encryption:', e);
  }
};

/**
 * Report encryption strategy usage for production monitoring
 * PRIVACY: Only reports strategy type, no user data
 */
export const reportEncryptionStrategy = strategy => {
  // Only report in production and avoid spamming
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production')
    return;

  try {
    // Use a simple approach that doesn't require external analytics
    const reportingKey = 'swts_encryption_strategy_reported';
    const lastReported = sessionStorage.getItem(reportingKey);
    const now = Date.now();

    // Only report once per session to avoid spam
    if (lastReported && now - parseInt(lastReported) < 3600000) {
      // 1 hour
      return;
    }

    // Report to debug log (only visible in development)
    debugLog('🔐 SWTS Encryption Strategy:', {
      strategy: strategy.strategy,
      reason: strategy.reason,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent.substring(0, 50) + '...', // Truncated for privacy
      hasEncryptionKey: strategy.hasEncryptionKey,
    });

    // Mark as reported for this session
    sessionStorage.setItem(reportingKey, now.toString());

    // Optional: Send to analytics if available (non-blocking)
    import('../../components/Analytics')
      .then(({ trackEvent }) => {
        trackEvent('encryption_strategy_detected', {
          encryption_strategy: strategy.strategy,
          encryption_reason: strategy.reason,
          has_encryption_key: strategy.hasEncryptionKey,
          non_interaction: true, // Don't affect bounce rate
        });
      })
      .catch(() => {
        // Silent fail - analytics shouldn't break functionality
      });
  } catch (error) {
    // Silent fail - monitoring shouldn't break functionality
  }
};

/**
 * Clear ALL encryption keys from all storage mechanisms
 * Used by clearUserData and emergency functions
 */
export const clearAllEncryptionKeys = () => {
  if (typeof window === 'undefined') return;

  try {
    // Clear encryption keys from all possible storage mechanisms
    try {
      sessionStorageHelpers.remove(ENCRYPTION_KEY_KEY);
    } catch (e) {
      // sessionStorage clear failed
    }

    try {
      localStorageHelpers.remove(ENCRYPTION_KEY_KEY);
    } catch (e) {
      // localStorage clear failed
    }

    try {
      cookieHelpers.remove(ENCRYPTION_KEY_KEY);
    } catch (e) {
      // Cookie clear failed
    }

    debugLog('🔑 All encryption keys cleared from all storage mechanisms');
  } catch (error) {
    debugError('Failed to clear encryption keys:', error);
  }
};

/**
 * Get comprehensive storage system status for debugging
 */
export const getStorageSystemStatus = () => {
  if (typeof window === 'undefined') {
    return {
      status: 'server_side',
      available: false,
      message: 'Storage not available server-side',
    };
  }

  const availability = getStorageAvailability();
  const environment = detectStorageEnvironment();
  const encryptionStrategy = getEncryptionStrategy();
  const diagnostics = getStorageDiagnostics();

  return {
    status: 'client_side',
    available: true,
    availability,
    environment,
    encryption: encryptionStrategy,
    diagnostics,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent.substring(0, 100) + '...',
  };
};
